import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Alert, Grid2, Paper, Stack, Typography, useTheme } from '@mui/material';
import { FormField, FormFieldType } from '@enroller/admissions-components';
import { usePageTitle } from '@enroller/utils';
import { useHeaderContext, ValidationType } from '../../../contexts/HeaderContext';
import { agreementText } from '../../constants';
import { AgreementFormValues, agreementSchema, defaultValues } from './AgreementFormValues';

export const AgreementForm = () => {
  const { setSubmit, setHeaderTitle } = useHeaderContext();
  const { setPageTitle } = usePageTitle();
  const theme = useTheme();
  const [validationMode, setValidationMode] = useState<ValidationType | ''>('');

  useEffect(() => {
    setHeaderTitle({ title: 'Agreement', subtitle: '' });
    setPageTitle('Agreement');
  }, [setHeaderTitle, setPageTitle]);

  const resolver = useMemo(() => {
    const currentSchema = validationMode === ValidationType.Full ? agreementSchema.required : agreementSchema.optional;
    return zodResolver(currentSchema);
  }, [validationMode]);

  const { control, handleSubmit } = useForm<AgreementFormValues>({ defaultValues, resolver });

  const onSubmit = useCallback(
    () =>
      handleSubmit((data) => {
        console.log('Form submitted:', data);
      })(),
    [handleSubmit],
  );

  // Submit form when resolver is changed.
  useEffect(() => {
    if (validationMode) {
      onSubmit();
      setValidationMode('');
    }
  }, [resolver, validationMode, onSubmit]);

  useEffect(() => {
    setSubmit(setValidationMode);
  }, [setSubmit, setValidationMode]);

  const [scrolledToBottom, setScrolledToBottom] = useState(false);

  const handleAgreementsScroll = (e: SyntheticEvent) => {
    const bottom = Math.floor(e.currentTarget.scrollHeight - e.currentTarget.scrollTop) === e.currentTarget.clientHeight;
    if (bottom) {
      setScrolledToBottom(true);
    }
  };

  return (
    <Stack component="form" onSubmit={onSubmit} gap={2}>
      <Paper sx={{ p: 2 }}>
        <Grid2 container spacing={3}>
          <Grid2 size={12}>
            <Typography variant="subtitle1">Host Family Agreement</Typography>
          </Grid2>
          <Grid2 size={12}>
            <Paper sx={{ maxHeight: '50vh', overflowY: 'scroll', p: 2 }} onScroll={handleAgreementsScroll}>
              <Typography variant="caption" color={theme.palette.grey[500]}>
                {agreementText}
              </Typography>
            </Paper>
          </Grid2>
          <Grid2 size={12}>
            <Alert variant="outlined" severity="info">
              <Typography variant="body2">
                By clicking the checkbox below I confirm that I am a host parent and that our family has read the information above and has received the U.S.
                Department of State Criteria for Secondary School Student Exchange Visitor Program. Furthermore, all members of our family agree that this is a
                worthwhile experience, that we wish to host a Greenheart student and that we are financially able to host. We verify that our Local Coordinator
                has interviewed with us in our home. We also verify that we are not related to the student(s) we will host.
              </Typography>
            </Alert>
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Text} label="Full Name" name="fullName" control={control} />
          </Grid2>
          <Grid2 size={4}>
            <FormField type={FormFieldType.Date} label="Date Signed" name="dateSigned" control={control} disabled />
          </Grid2>
          <Grid2 size={12}>
            <FormField
              control={control}
              name="agreed"
              type={FormFieldType.Checkbox}
              label="I agree to the terms set out in the Host Family Agreement above"
              disabled={!scrolledToBottom}
            />
          </Grid2>
        </Grid2>
      </Paper>
    </Stack>
  );
};
